/**
 * Camera: deadzone follow, velocity lookahead, trauma-based shake.
 *
 * Two decisions that matter for a side-view action game:
 *
 *  - **Lookahead follows velocity, not facing.** Facing-based lookahead swings
 *    the whole screen every time the player turns around, which is nauseating
 *    during combat. Velocity-based lookahead only moves when you're committed
 *    to a direction.
 *
 *  - **Shake is trauma-squared.** Storing trauma and shaking by trauma² means
 *    small impacts read as almost nothing while big ones are violent, without
 *    needing separate small/large shake paths.
 *
 * And one that matters because it cost an entire issue to find — issue #76.
 *
 *  - **The camera is a world-coordinate source, and it must never emit a
 *    coordinate that is not a place.** `BackdropRenderer.render` reads
 *    `world.biomeAt(camera.renderX, camera.renderY).parallax` and
 *    `Game.stepSimulation` generates chunks around the same pair, so whatever
 *    this class holds *is* where the world thinks the player is.
 *
 *    Every accumulator here is exponential — `damp(a, b, rate, dt)` is
 *    `b + (a - b) * pow(rate, dt)` — which means it has no way back from a
 *    non-finite value: once `a` or `b` is `NaN` the result is `NaN`, and next
 *    frame that `NaN` is `a`. **One bad frame is permanent.** Worse, the input
 *    most likely to be bad is a velocity, and a bad velocity in the entity table
 *    heals on the very next substep (`approach(NaN, target, d)` returns
 *    `target`), so the entity looks fine while the camera stays broken for the
 *    rest of the run and hands the world a non-place sixty times a second.
 *
 *    That is the mechanism behind #71's `biomeAt` crash. So the inputs are
 *    checked at entry, a bad one is *refused* rather than integrated, and the
 *    refusal is **recorded** in `faults` rather than swallowed. Recording is the
 *    point: #71 made the world lookup total, which stopped the crash and thereby
 *    hid the cause for an entire issue. A repair nobody can see is how a bug
 *    outlives its own fix.
 */

import { clamp, clamp01, damp } from '../core/math';
import { fxRng } from '../core/rng';

/**
 * Non-finite inputs this camera refused, for the life of the camera.
 *
 * Deliberately not reset per frame — `profiler`'s counters are, which is right
 * for "draw calls this frame" and exactly wrong here: a one-frame fault that
 * leaves no trace is the failure mode #76 is about. The first fault is kept
 * separately from the last because the first one is the cause and everything
 * after it is consequence.
 *
 * No clock and no console, so this reads identically in a headless test and on
 * the F3 overlay.
 */
export interface CameraFaults {
  count: number;
  /** Name of the first term that arrived non-finite. Empty while clean. */
  first: string;
  firstValue: number;
  /** Name of the most recent one, so a live overlay shows current behaviour. */
  last: string;
  lastValue: number;
}

export class Camera {
  /** World-space centre. */
  x = 0;
  y = 0;

  /** Units visible vertically. Width follows from aspect. */
  zoom = 1;
  viewHeight = 270;

  /** Pixels per world unit, derived at update time. */
  scale = 1;
  viewW = 480;
  viewH = 270;

  private targetX = 0;
  private targetY = 0;
  private lookX = 0;
  private lookY = 0;

  /** Deadzone half-extents in world units. */
  deadzoneX = 22;
  deadzoneY = 16;

  /** 0..1, decays continuously. Shake magnitude is trauma². */
  trauma = 0;

  /**
   * Global multiplier on every trauma source. 1 is the authored strength.
   *
   * It exists so the shake can be turned down from *outside* the simulation:
   * `Game` calls `shake()` from a dozen places with authored amounts, and a
   * settings row that had to edit those would be a balance change rather than
   * a comfort option. Scaling here leaves every call site's relative weight
   * intact — a big hit still reads bigger than a small one at half strength —
   * and 0 stops the camera moving at all rather than merely damping it.
   */
  shakeScale = 1;
  private shakeX = 0;
  private shakeY = 0;
  private shakeSeed = 0;

  /** Offset applied after shake — for recoil kicks, which should be directional. */
  private kickX = 0;
  private kickY = 0;

  /**
   * Last usable aspect, so a frame whose canvas has no size holds the previous
   * shape instead of reshaping the view to a non-number. 16:9 until the first
   * real frame arrives.
   */
  private lastAspect = 16 / 9;

  /** Clamp the camera into world bounds when a room defines them. */
  boundsMinX = -Infinity;
  boundsMinY = -Infinity;
  boundsMaxX = Infinity;
  boundsMaxY = Infinity;

  /** Every non-finite input refused since construction. See `CameraFaults`. */
  readonly faults: CameraFaults = {
    count: 0,
    first: '',
    firstValue: NaN,
    last: '',
    lastValue: NaN,
  };

  /**
   * The gate every input passes through.
   *
   * Returns `v` when it is a real number; otherwise records the refusal under
   * `term` and returns `fallback`. The name is what makes the record worth
   * having: "the camera refused something" is a shrug, "`focusVx` arrived `NaN`"
   * points at the entity table.
   *
   * `Number.isFinite` rather than a comparison pair, because a comparison pair
   * is precisely what let `NaN` through the band clamp in #71 — a `NaN` is
   * neither `< lo` nor `> hi`, so a two-comparison clamp launders it instead of
   * catching it.
   */
  private take(term: string, v: number, fallback: number): number {
    if (Number.isFinite(v)) return v;
    const f = this.faults;
    if (f.count === 0) {
      f.first = term;
      f.firstValue = v;
    }
    f.count++;
    f.last = term;
    f.lastValue = v;
    return fallback;
  }

  snapTo(x: number, y: number): void {
    // A refused snap leaves the camera where it was, which is wrong by however
    // far the teleport was — and still enormously better than a camera that can
    // never be finite again. `Game.spawnPlayer` and `Game.groundPlayer` both
    // snap, so a bad spawn point would otherwise poison the run from frame one.
    this.x = this.targetX = this.take('snapToX', x, this.x);
    this.y = this.targetY = this.take('snapToY', y, this.y);
    this.lookX = this.lookY = 0;
  }

  /**
   * Add screen trauma. `amount` 0..1; multiple sources accumulate.
   *
   * The check matters more than it looks. `clamp01(trauma + NaN)` is `NaN`,
   * `Math.max(0, NaN)` is `NaN`, and `mag > 0.0001` is *false* for `NaN` — so a
   * single bad amount does not shake the screen wrongly, it stops the screen
   * shaking **ever again, for the rest of the session**, with nothing on screen
   * to say why. `shakeScale` is checked too: it is written from the settings
   * panel, which is outside the simulation.
   */
  shake(amount: number): void {
    const scale = this.take('shakeScale', this.shakeScale, 1);
    this.trauma = clamp01(this.trauma + this.take('shakeAmount', amount, 0) * scale);
  }

  /** Directional impulse — weapon recoil, heavy landings. */
  kick(dx: number, dy: number): void {
    // Written from `wielder.ts`'s `onCameraKick` as `dx * amount`, where all
    // three terms come from weapon data. It decays by multiplication, so a bad
    // one never leaves.
    this.kickX += this.take('kickX', dx, 0);
    this.kickY += this.take('kickY', dy, 0);
  }

  update(
    rawFocusX: number,
    rawFocusY: number,
    rawFocusVx: number,
    rawFocusVy: number,
    rawAspect: number,
    rawDt: number,
  ): void {
    // Six inputs, six gates. Every one of them crosses a system boundary — the
    // first four are read straight out of the entity table, `aspect` is
    // `glc.width / glc.height` (which is `0 / 0` on a canvas that has not been
    // laid out yet), and `dt` is the frame clock. The fallbacks are chosen so a
    // refused frame is a *still* frame rather than a wrong one.
    const focusX = this.take('focusX', rawFocusX, this.targetX);
    const focusY = this.take('focusY', rawFocusY, this.targetY);
    const focusVx = this.take('focusVx', rawFocusVx, 0);
    const focusVy = this.take('focusVy', rawFocusVy, 0);
    const aspect = this.take('aspect', rawAspect, this.lastAspect);
    const dt = this.take('dt', rawDt, 0);
    this.lastAspect = aspect;

    // `zoom` is a public field the settings panel writes; zero would make the
    // view infinitely tall and `visibleRect` unusable for terrain and light
    // culling, which cull against it every frame.
    this.viewH = this.take('viewH', this.viewHeight / this.zoom, this.viewH);
    this.viewW = this.viewH * aspect;

    // Deadzone: the camera doesn't move until the target leaves a box around
    // its centre, which keeps small platforming adjustments from jittering it.
    const dx = focusX - this.targetX;
    const dy = focusY - this.targetY;
    if (dx > this.deadzoneX) this.targetX = focusX - this.deadzoneX;
    else if (dx < -this.deadzoneX) this.targetX = focusX + this.deadzoneX;
    if (dy > this.deadzoneY) this.targetY = focusY - this.deadzoneY;
    else if (dy < -this.deadzoneY) this.targetY = focusY + this.deadzoneY;

    // Lookahead proportional to velocity, capped and smoothed.
    const wantLookX = clamp(focusVx * 0.22, -this.viewW * 0.2, this.viewW * 0.2);
    // Vertical lookahead is deliberately weaker and asymmetric: showing more
    // ground while falling is useful, showing more sky while rising is not.
    const wantLookY = clamp(focusVy * (focusVy > 0 ? 0.14 : 0.06), -20, this.viewH * 0.18);
    this.lookX = damp(this.lookX, wantLookX, 0.0015, dt);
    this.lookY = damp(this.lookY, wantLookY, 0.004, dt);

    const goalX = this.targetX + this.lookX;
    const goalY = this.targetY + this.lookY;

    this.x = damp(this.x, goalX, 0.00004, dt);
    this.y = damp(this.y, goalY, 0.0002, dt);

    // Bounds clamp accounts for the visible extent, so the view never shows
    // outside a bounded room.
    const halfW = this.viewW / 2;
    const halfH = this.viewH / 2;
    if (this.boundsMaxX - this.boundsMinX > this.viewW) {
      this.x = clamp(this.x, this.boundsMinX + halfW, this.boundsMaxX - halfW);
    }
    if (this.boundsMaxY - this.boundsMinY > this.viewH) {
      this.y = clamp(this.y, this.boundsMinY + halfH, this.boundsMaxY - halfH);
    }

    // Trauma decays linearly; magnitude is trauma² so small hits stay subtle.
    this.trauma = Math.max(0, this.trauma - dt * 1.6);
    const mag = this.trauma * this.trauma;
    this.shakeSeed += dt * 34;
    if (mag > 0.0001) {
      this.shakeX = (fxRng.float() * 2 - 1) * mag * 14;
      this.shakeY = (fxRng.float() * 2 - 1) * mag * 14;
    } else {
      this.shakeX = this.shakeY = 0;
    }

    this.kickX = damp(this.kickX, 0, 0.00002, dt);
    this.kickY = damp(this.kickY, 0, 0.00002, dt);

    this.scale = 1 / this.viewH;
  }

  /** Final render position including shake and kick. */
  get renderX(): number {
    return this.x + this.shakeX + this.kickX;
  }
  get renderY(): number {
    return this.y + this.shakeY + this.kickY;
  }

  /** World → normalised device coordinates. */
  worldToNdc(wx: number, wy: number, out: { x: number; y: number }): void {
    out.x = ((wx - this.renderX) / (this.viewW / 2));
    out.y = -((wy - this.renderY) / (this.viewH / 2));
  }

  /** Screen (CSS pixels) → world. Used for aiming. */
  screenToWorld(
    sx: number,
    sy: number,
    screenW: number,
    screenH: number,
    out: { x: number; y: number },
  ): void {
    out.x = this.renderX + (sx / screenW - 0.5) * this.viewW;
    out.y = this.renderY + (sy / screenH - 0.5) * this.viewH;
  }

  /**
   * Visible world rect, dilated by `pad` units.
   *
   * Returns a reused struct rather than a fresh literal: this is called once
   * per frame from the terrain pass, and the renderer's whole allocation story
   * is that the steady-state frame allocates nothing. Callers must read the
   * fields before the next call rather than retaining the object.
   */
  private readonly rect = { x0: 0, y0: 0, x1: 0, y1: 0 };
  visibleRect(pad = 0): { x0: number; y0: number; x1: number; y1: number } {
    const hw = this.viewW / 2 + pad;
    const hh = this.viewH / 2 + pad;
    const r = this.rect;
    r.x0 = this.renderX - hw;
    r.y0 = this.renderY - hh;
    r.x1 = this.renderX + hw;
    r.y1 = this.renderY + hh;
    return r;
  }
}
